import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const API = 'https://api.medlynk.me/api';

function App() {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || null);
  const [page, setPage] = useState('dashboard');
  const [phone, setPhone] = useState('+237');
  const [otp, setOtp] = useState('');
  const [authStep, setAuthStep] = useState('phone');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [pharmacies, setPharmacies] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [searchLogs, setSearchLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [lang, setLang] = useState('fr');

const t = {
  fr: {
    dashboard: 'Tableau de bord', pharmacies: 'Pharmacies', facilities: 'Établissements',
    verification: 'File de vérification', analytics: 'Analyses', settings: 'Paramètres',
    users: 'Utilisateurs', active: 'actives', verified: 'vérifiées', medicines: 'Médicaments',
    categories: 'catégories', pending: 'En attente', unverified: 'non vérifiées',
    recent: 'récentes', quickActions: 'Actions rapides', verifyNow: 'Vérifier maintenant',
    allVerified: 'Toutes les pharmacies sont vérifiées', noUnverified: 'Aucune en attente',
    coverage: 'Couverture par quartier', distribution: 'Distribution des établissements',
    platform: 'Paramètres de la plateforme', verifyRate: 'Taux de vérification',
    referenced: 'référencés', phone: 'Téléphone', status: 'Statut', actions: 'Actions',
    address: 'Adresse', type: 'Type', services: 'Services', quarter: 'Quartier',
    name: 'Nom', role: 'Rôle', city: 'Ville', joined: 'Inscrit', total: 'total',
    logout: 'Se déconnecter', adminPhone: 'Numéro administrateur', sendCode: 'Envoyer le code',
    verify: 'Vérifier', changeNumber: 'Changer le numéro', codeSent: 'Code envoyé à',
    sending: 'Envoi...', verifying: 'Vérification...', loading: 'Chargement...',
    refresh: 'Actualiser', accessDenied: 'Accès refusé', notAdmin: 'Ce numéro n\'est pas autorisé comme administrateur.',
  },
  en: {
    dashboard: 'Dashboard', pharmacies: 'Pharmacies', facilities: 'Facilities',
    verification: 'Verification Queue', analytics: 'Analytics', settings: 'Settings',
    users: 'Users', active: 'active', verified: 'verified', medicines: 'Medicines',
    categories: 'categories', pending: 'Pending', unverified: 'unverified',
    recent: 'recent', quickActions: 'Quick Actions', verifyNow: 'Verify now',
    allVerified: 'All pharmacies are verified', noUnverified: 'None pending',
    coverage: 'Coverage by quarter', distribution: 'Facility distribution',
    platform: 'Platform Settings', verifyRate: 'Verification rate',
    referenced: 'referenced', phone: 'Phone', status: 'Status', actions: 'Actions',
    address: 'Address', type: 'Type', services: 'Services', quarter: 'Quarter',
    name: 'Name', role: 'Role', city: 'City', joined: 'Joined', total: 'total',
    logout: 'Sign out', adminPhone: 'Admin phone number', sendCode: 'Send code',
    verify: 'Verify', changeNumber: 'Change number', codeSent: 'Code sent to',
    sending: 'Sending...', verifying: 'Verifying...', loading: 'Loading...',
    refresh: 'Refresh', accessDenied: 'Access Denied', notAdmin: 'This number is not authorized as admin.',
  }
};

const L = t[lang];
const ADMIN_PHONES = ['+237677408458'];

  useEffect(() => {
    if (token) loadDashboard();
  }, [token]);

  const requestOTP = async () => {
    if (phone.length < 10) return;
    try {
      setLoading(true);
      const res = await axios.post(`${API}/users/request-otp`, { phone_number: phone, language: 'fr' });
      if (res.data.success) {
        setAuthStep('otp');
        if (res.data.dev_otp) setOtp(res.data.dev_otp);
      }
    } catch (err) { alert('Erreur: ' + err.message); }
    finally { setLoading(false); }
  };

  const verifyOTP = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/users/verify-otp`, { phone_number: phone, otp_code: otp });
      if (res.data.success) {
        if (!ADMIN_PHONES.includes(phone)) {
          alert(L.notAdmin);
          setLoading(false);
          return;
        }
        localStorage.setItem('admin_token', res.data.token);
        setToken(res.data.token);
        setAuthStep('done');
      }
    } catch (err) { alert('Code invalide'); }
    finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setAuthStep('phone');
  };

  const loadDashboard = async () => {
    try {
      setLoading(true);

      const phRes = await axios.get(`${API}/pharmacies`);
      const allPharmacies = phRes.data.pharmacies || [];
      setPharmacies(allPharmacies);

      const facRes = await axios.get(`${API}/facilities?city=Douala`);
      const allFacilities = facRes.data.facilities || [];
      setFacilities(allFacilities);

      const catRes = await axios.get(`${API}/medicines/categories`);
      const medRes = await axios.get(`${API}/medicines`);

      try {
        const headers = { Authorization: `Bearer ${token}` };
        const usersQuery = await axios.get(`${API}/users/profile`, { headers });
        // For now we just track that we can auth
      } catch (e) {}

      setStats({
        totalPharmacies: allPharmacies.length,
        verifiedPharmacies: allPharmacies.filter(p => p.listing_status === 'verified').length,
        publicPharmacies: allPharmacies.filter(p => p.listing_status === 'public').length,
        totalFacilities: allFacilities.length,
        verifiedFacilities: allFacilities.filter(f => f.listing_status === 'verified').length,
        totalMedicines: medRes.data.total || 0,
        totalCategories: catRes.data.total || 0,
      });

    } catch (err) { console.log('Load error:', err.message); }
    finally { setLoading(false); }
  };

  const verifyPharmacy = async (id) => {
    try {
      await axios.put(`${API}/pharmacies/${id}/verify`);
      loadDashboard();
    } catch (err) { alert('Erreur: ' + err.message); }
  };

  // LOGIN
  if (!token || authStep !== 'done') {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <span className="logo-icon">🛡️</span>
            <h1>Med<span>Lynk</span></h1>
            <p className="login-sub">Operations Console</p>
            <div style={{display:'flex',gap:'6px',justifyContent:'center',marginTop:'12px'}}>
              <button onClick={() => setLang('fr')} style={{padding:'5px 14px',borderRadius:'20px',border: lang==='fr' ? '2px solid #0A2A24' : '1px solid #E5E7EB',background: lang==='fr' ? '#0A2A24' : 'transparent',color: lang==='fr' ? '#fff' : '#6B7280',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>FR</button>
              <button onClick={() => setLang('en')} style={{padding:'5px 14px',borderRadius:'20px',border: lang==='en' ? '2px solid #0A2A24' : '1px solid #E5E7EB',background: lang==='en' ? '#0A2A24' : 'transparent',color: lang==='en' ? '#fff' : '#6B7280',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>EN</button>
            </div>
          </div>
          {authStep === 'phone' && (
            <>
              <label>Numéro administrateur</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+237 6XX XXX XXX" />
              <button className="btn-primary" onClick={requestOTP} disabled={loading}>
                {loading ? 'Envoi...' : 'Envoyer le code'}
              </button>
            </>
          )}
          {authStep === 'otp' && (
            <>
              <p className="otp-hint">Code envoyé à {phone}</p>
              <input type="text" value={otp} onChange={e => setOtp(e.target.value)} placeholder="XXXXXX" maxLength={6} />
              <button className="btn-primary" onClick={verifyOTP} disabled={loading}>
                {loading ? 'Vérification...' : 'Vérifier'}
              </button>
              <button className="btn-link" onClick={() => setAuthStep('phone')}>← Changer le numéro</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // DASHBOARD
  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="logo-icon-sm">🛡️</span>
          <div>
            <h2>MedLynk</h2>
            <p>Operations Console</p>
          </div>
        </div>
        <nav className="sidebar-nav">
          <button className={page === 'dashboard' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('dashboard')}>📊 {L.dashboard}</button>
          <button className={page === 'pharmacies' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('pharmacies')}>💊 {L.pharmacies}</button>
          <button className={page === 'facilities' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('facilities')}>🏥 {L.facilities}</button>
          <button className={page === 'verification' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('verification')}>✓ {L.verification}</button>
          <button className={page === 'users' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('users')}>👥 {L.users}</button>
          <button className={page === 'analytics' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('analytics')}>📈 {L.analytics}</button>
          <button className={page === 'settings' ? 'nav-btn active' : 'nav-btn'} onClick={() => setPage('settings')}>⚙️ {L.settings}</button>
        </nav>
        <div className="sidebar-facility">
          <strong>MedLynk Operations</strong>
          <span>Douala, Cameroun</span>
          <span className="verified-badge">Stage 1</span>
        </div>
        <div style={{display:'flex',gap:'6px',justifyContent:'center',marginBottom:'12px'}}>
          <button onClick={() => setLang('fr')} style={{padding:'5px 14px',borderRadius:'20px',border: lang==='fr' ? '2px solid #0A2A24' : '1px solid #E5E7EB',background: lang==='fr' ? '#0A2A24' : 'transparent',color: lang==='fr' ? '#fff' : '#6B7280',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>FR</button>
          <button onClick={() => setLang('en')} style={{padding:'5px 14px',borderRadius:'20px',border: lang==='en' ? '2px solid #0A2A24' : '1px solid #E5E7EB',background: lang==='en' ? '#0A2A24' : 'transparent',color: lang==='en' ? '#fff' : '#6B7280',cursor:'pointer',fontSize:'12px',fontWeight:'600'}}>EN</button>
        </div>
        <button className="btn-logout" onClick={logout}>{L.logout}</button>
      </aside>

      <main className="main-content">
        <header className="top-bar">
          <h1>{page === 'dashboard' ? 'Operations Dashboard' : page === 'pharmacies' ? 'Gestion des Pharmacies' : page === 'facilities' ? 'Gestion des Établissements' : page === 'verification' ? 'File de Vérification' : page === 'analytics' ? 'Analyses et Statistiques' : 'Paramètres'}</h1>
          <p>MedLynk Operations — Douala, Cameroun</p>
        </header>

        {loading && <div className="loading">Chargement...</div>}

        {/* DASHBOARD */}
        {page === 'dashboard' && !loading && stats && (
          <div className="dashboard">
            <div className="stats-grid">
              <div className="stat-card green">
                <p className="stat-label">Pharmacies actives</p>
                <h2 className="stat-value">{stats.totalPharmacies}</h2>
                <span className="stat-sub">{stats.verifiedPharmacies} vérifiées</span>
              </div>
              <div className="stat-card blue">
                <p className="stat-label">Établissements</p>
                <h2 className="stat-value">{stats.totalFacilities}</h2>
                <span className="stat-sub">{stats.verifiedFacilities} vérifiés</span>
              </div>
              <div className="stat-card">
                <p className="stat-label">Médicaments</p>
                <h2 className="stat-value">{stats.totalMedicines}</h2>
                <span className="stat-sub">{stats.totalCategories} catégories</span>
              </div>
              <div className="stat-card amber">
                <p className="stat-label">En attente</p>
                <h2 className="stat-value">{stats.publicPharmacies}</h2>
                <span className="stat-sub">non vérifiées</span>
              </div>
            </div>

            <div className="info-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px'}}>
              <div className="dashboard-section">
                <h3>Pharmacies récentes</h3>
                {pharmacies.slice(0, 5).map((p, i) => (
                  <div key={i} className="stock-row">
                    <div className="stock-info">
                      <strong>{p.name_fr}</strong>
                      <span>{p.quarter} — {p.listing_status}</span>
                    </div>
                    <span className="freshness-badge" style={{
                      background: p.listing_status === 'verified' ? '#D1FAE5' : '#FEF3C7',
                      color: p.listing_status === 'verified' ? '#059669' : '#D97706'
                    }}>{p.listing_status === 'verified' ? '✓ Vérifié' : 'Public'}</span>
                  </div>
                ))}
              </div>
              <div className="dashboard-section">
                <h3>Établissements récents</h3>
                {facilities.slice(0, 5).map((f, i) => (
                  <div key={i} className="stock-row">
                    <div className="stock-info">
                      <strong>{f.name_fr}</strong>
                      <span>{f.quarter} — {f.type_fr || 'Établissement'}</span>
                    </div>
                    {f.is_emergency && <span className="freshness-badge" style={{background:'#FEE2E2',color:'#DC2626'}}>🚨 Urgences</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className="dashboard-section">
              <h3>Actions rapides</h3>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))',gap:'10px'}}>
                <button className="action-btn" onClick={() => setPage('verification')}>✓ Vérifier les pharmacies</button>
                <button className="action-btn" onClick={() => setPage('pharmacies')}>💊 Voir toutes les pharmacies</button>
                <button className="action-btn" onClick={() => setPage('facilities')}>🏥 Voir les établissements</button>
                <button className="action-btn" onClick={() => setPage('analytics')}>📈 Voir les analyses</button>
              </div>
            </div>
          </div>
        )}

        {/* PHARMACIES LIST */}
        {page === 'pharmacies' && !loading && (
          <div>
            <div className="stock-header">
              <h3>{pharmacies.length} pharmacies au total</h3>
              <button className="btn-refresh" onClick={loadDashboard}>🔄 Actualiser</button>
            </div>
            <table className="stock-table">
              <thead>
                <tr>
                  <th>Pharmacie</th>
                  <th>Quartier</th>
                  <th>Téléphone</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pharmacies.map((p, i) => (
                  <tr key={i}>
                    <td><strong>{p.name_fr}</strong><br/><span className="text-muted">{p.name_en}</span></td>
                    <td>{p.quarter}, {p.city}</td>
                    <td>{p.phone_number}</td>
                    <td>
                      <span className="freshness-badge" style={{
                        background: p.listing_status === 'verified' ? '#D1FAE5' : p.listing_status === 'claimed' ? '#DBEAFE' : '#FEF3C7',
                        color: p.listing_status === 'verified' ? '#059669' : p.listing_status === 'claimed' ? '#2563EB' : '#D97706'
                      }}>{p.listing_status === 'verified' ? '✓ Vérifié' : p.listing_status === 'claimed' ? '📋 Réclamé' : '🔵 Public'}</span>
                    </td>
                    <td>
                      {p.listing_status !== 'verified' && (
                        <button className="btn-confirm" onClick={() => verifyPharmacy(p.id)}>✓ Vérifier</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* FACILITIES LIST */}
        {page === 'facilities' && !loading && (
          <div>
            <div className="stock-header">
              <h3>{facilities.length} établissements</h3>
              <button className="btn-refresh" onClick={loadDashboard}>🔄 Actualiser</button>
            </div>
            <table className="stock-table">
              <thead>
                <tr>
                  <th>Établissement</th>
                  <th>Type</th>
                  <th>Quartier</th>
                  <th>Téléphone</th>
                  <th>Services</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {facilities.map((f, i) => (
                  <tr key={i}>
                    <td><strong>{f.name_fr}</strong><br/><span className="text-muted">{f.name_en}</span></td>
                    <td>{f.type_fr || 'Établissement'}</td>
                    <td>{f.quarter}, {f.city}</td>
                    <td>{f.phone_number}</td>
                    <td>
                      {f.is_emergency && <span className="tag red" style={{display:'inline-block',margin:'2px',padding:'2px 8px',borderRadius:'6px',fontSize:'11px',background:'#FEE2E2',color:'#DC2626'}}>🚨</span>}
                      {f.has_lab && <span className="tag" style={{display:'inline-block',margin:'2px',padding:'2px 8px',borderRadius:'6px',fontSize:'11px',background:'#CCFBF1',color:'#0D9488'}}>🔬</span>}
                      {f.has_pharmacy && <span className="tag" style={{display:'inline-block',margin:'2px',padding:'2px 8px',borderRadius:'6px',fontSize:'11px',background:'#D1FAE5',color:'#059669'}}>💊</span>}
                    </td>
                    <td>
                      <span className="freshness-badge" style={{
                        background: f.listing_status === 'verified' ? '#D1FAE5' : '#FEF3C7',
                        color: f.listing_status === 'verified' ? '#059669' : '#D97706'
                      }}>{f.listing_status === 'verified' ? '✓ Vérifié' : 'Public'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* VERIFICATION QUEUE */}
        {page === 'verification' && !loading && (
          <div>
            <h3 style={{marginBottom:'16px'}}>Pharmacies en attente de vérification</h3>
            {pharmacies.filter(p => p.listing_status !== 'verified').length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">✅</span>
                <h3>Toutes les pharmacies sont vérifiées</h3>
                <p>Aucune pharmacie en attente de vérification.</p>
              </div>
            ) : (
              <table className="stock-table">
                <thead>
                  <tr>
                    <th>Pharmacie</th>
                    <th>Adresse</th>
                    <th>Téléphone</th>
                    <th>Statut actuel</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pharmacies.filter(p => p.listing_status !== 'verified').map((p, i) => (
                    <tr key={i}>
                      <td><strong>{p.name_fr}</strong></td>
                      <td>{p.address}, {p.quarter}</td>
                      <td>{p.phone_number}</td>
                      <td><span className="freshness-badge" style={{background:'#FEF3C7',color:'#D97706'}}>{p.listing_status}</span></td>
                      <td><button className="btn-confirm" onClick={() => verifyPharmacy(p.id)}>✓ Vérifier maintenant</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h3 style={{marginTop:'32px',marginBottom:'16px'}}>Établissements en attente</h3>
            {facilities.filter(f => f.listing_status !== 'verified').length === 0 ? (
              <p className="text-muted">Tous les établissements sont vérifiés.</p>
            ) : (
              <table className="stock-table">
                <thead>
                  <tr>
                    <th>Établissement</th>
                    <th>Type</th>
                    <th>Adresse</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {facilities.filter(f => f.listing_status !== 'verified').map((f, i) => (
                    <tr key={i}>
                      <td><strong>{f.name_fr}</strong></td>
                      <td>{f.type_fr || 'Établissement'}</td>
                      <td>{f.address}, {f.quarter}</td>
                      <td><span className="freshness-badge" style={{background:'#FEF3C7',color:'#D97706'}}>{f.listing_status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ANALYTICS */}
        {page === 'analytics' && !loading && stats && (
          <div className="dashboard">
            <h3 style={{marginBottom:'16px'}}>Statistiques de la plateforme</h3>
            <div className="stats-grid">
              <div className="stat-card green">
                <p className="stat-label">Pharmacies vérifiées</p>
                <h2 className="stat-value">{stats.verifiedPharmacies}</h2>
                <span className="stat-sub">sur {stats.totalPharmacies} total</span>
              </div>
              <div className="stat-card blue">
                <p className="stat-label">Établissements vérifiés</p>
                <h2 className="stat-value">{stats.verifiedFacilities}</h2>
                <span className="stat-sub">sur {stats.totalFacilities} total</span>
              </div>
              <div className="stat-card">
                <p className="stat-label">Médicaments référencés</p>
                <h2 className="stat-value">{stats.totalMedicines}</h2>
                <span className="stat-sub">{stats.totalCategories} catégories</span>
              </div>
              <div className="stat-card amber">
                <p className="stat-label">Taux de vérification</p>
                <h2 className="stat-value">{stats.totalPharmacies > 0 ? Math.round(stats.verifiedPharmacies / stats.totalPharmacies * 100) : 0}%</h2>
                <span className="stat-sub">pharmacies</span>
              </div>
            </div>

            <div className="dashboard-section" style={{marginTop:'20px'}}>
              <h3>Couverture par quartier</h3>
              <div style={{display:'flex',flexWrap:'wrap',gap:'8px',marginTop:'12px'}}>
                {[...new Set(pharmacies.map(p => p.quarter))].sort().map((q, i) => {
                  const count = pharmacies.filter(p => p.quarter === q).length;
                  return (
                    <span key={i} style={{padding:'6px 14px',background:'#F0FDF4',borderRadius:'10px',fontSize:'13px',fontWeight:'600',color:'#0D9488'}}>
                      {q} ({count})
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="dashboard-section" style={{marginTop:'20px'}}>
              <h3>Distribution des établissements</h3>
              <div style={{display:'flex',flexWrap:'wrap',gap:'8px',marginTop:'12px'}}>
                {facilities.map((f, i) => (
                  <span key={i} style={{padding:'6px 14px',background:f.is_emergency ? '#FEE2E2' : '#EDE9FE',borderRadius:'10px',fontSize:'13px',fontWeight:'600',color:f.is_emergency ? '#DC2626' : '#6C3483'}}>
                    {f.name_fr} — {f.quarter}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

                {/* USERS */}
        {page === 'users' && !loading && (
          <div>
            <div className="stock-header">
              <h3>{pharmacies.length + facilities.length} {lang === 'fr' ? 'organisations enregistrées' : 'registered organizations'}</h3>
              <button className="btn-refresh" onClick={loadDashboard}>🔄 {L.refresh}</button>
            </div>
            <h3 style={{margin:'16px 0 8px'}}>{L.pharmacies} ({pharmacies.length})</h3>
            <table className="stock-table">
              <thead>
                <tr>
                  <th>{L.name}</th>
                  <th>{L.quarter}</th>
                  <th>{L.phone}</th>
                  <th>{L.status}</th>
                </tr>
              </thead>
              <tbody>
                {pharmacies.map((p, i) => (
                  <tr key={i}>
                    <td><strong>{p.name_fr}</strong></td>
                    <td>{p.quarter}, {p.city}</td>
                    <td>{p.phone_number}</td>
                    <td><span className="freshness-badge" style={{background: p.listing_status === 'verified' ? '#D1FAE5' : '#FEF3C7', color: p.listing_status === 'verified' ? '#059669' : '#D97706'}}>{p.listing_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h3 style={{margin:'24px 0 8px'}}>{L.facilities} ({facilities.length})</h3>
            <table className="stock-table">
              <thead>
                <tr>
                  <th>{L.name}</th>
                  <th>{L.type}</th>
                  <th>{L.quarter}</th>
                  <th>{L.phone}</th>
                  <th>{L.services}</th>
                </tr>
              </thead>
              <tbody>
                {facilities.map((f, i) => (
                  <tr key={i}>
                    <td><strong>{f.name_fr}</strong></td>
                    <td>{f.type_fr || 'Établissement'}</td>
                    <td>{f.quarter}</td>
                    <td>{f.phone_number}</td>
                    <td>
                      {f.is_emergency && <span style={{display:'inline-block',margin:'2px',padding:'2px 8px',borderRadius:'6px',fontSize:'11px',background:'#FEE2E2',color:'#DC2626'}}>🚨</span>}
                      {f.has_lab && <span style={{display:'inline-block',margin:'2px',padding:'2px 8px',borderRadius:'6px',fontSize:'11px',background:'#CCFBF1',color:'#0D9488'}}>🔬</span>}
                      {f.has_pharmacy && <span style={{display:'inline-block',margin:'2px',padding:'2px 8px',borderRadius:'6px',fontSize:'11px',background:'#D1FAE5',color:'#059669'}}>💊</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* SETTINGS */}
        {page === 'settings' && (
          <div className="settings-page">
            <div className="profile-card">
              <h3>Paramètres de la plateforme</h3>
              <div className="detail-row"><span>🌐 Plateforme</span><strong>MedLynk Africa</strong></div>
              <div className="detail-row"><span>🏙️ Ville active</span><strong>Douala</strong></div>
              <div className="detail-row"><span>📋 Stage</span><strong>Stage 1 — Verified Healthcare Access Network</strong></div>
              <div className="detail-row"><span>🔗 API</span><strong>api.medlynk.me</strong></div>
              <div className="detail-row"><span>📱 App patients</span><strong>app.medlynk.me</strong></div>
              <div className="detail-row"><span>💊 Pharmacies</span><strong>pharmacy.medlynk.me</strong></div>
              <div className="detail-row"><span>🏥 Établissements</span><strong>facility.medlynk.me</strong></div>
              <div className="detail-row"><span>📱 Version</span><strong>1.0.0</strong></div>
              <div className="detail-row"><span>☁️ Cloud</span><strong>AWS EC2 + RDS — Paris (eu-west-3)</strong></div>
              <div className="detail-row"><span>💰 Crédits AWS</span><strong>$1,000 — expire juin 2028</strong></div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;